/**
 * Career Path visualization — the "people pave your path" metaphor made
 * tactile. Renders a horizontal track split into 4 zones (Cold → Warming →
 * Active → Allies). Each contact is positioned on the track by their
 * fit_score (x-axis) and colour-coded by recency of contact (staleness).
 *
 * Layout
 * ──────
 *   Y-axis (top):   ┌── Unranked rail (no fit_score yet) ──┐
 *                   │   small grey ovals + "Analyze"        │
 *                   └───────────────────────────────────────┘
 *   Y-axis (mid):    Path track       ◯  ◯   ◯◯   ◯
 *                    [Cold][Warming][Active][Allies]
 *   Y-axis (btm):    Selected-stone insight panel (lazy)
 *
 * Stones with last_contacted_at > 60d glow amber to nudge follow-ups.
 */
import React, { useMemo, useState } from "react";
import { Sparkles, ExternalLink, ChevronDown, CheckCircle2, Loader, AlertTriangle, MapPin } from "lucide-react";
import { api, friendlyAxiosError } from "../../lib/api";
import { PATH_ZONES, zoneForScore, daysSinceContact, stalenessTint } from "./utils";

const TRACK_HEIGHT = 110;
const STONE_RADIUS = 18;

export default function CareerPath({ contacts, onChanged }) {
  const [selectedId, setSelectedId] = useState(null);
  const [hoverId, setHoverId] = useState(null);

  // Split into ranked (have fit_score) + unranked
  const { ranked, unranked, overdueCount } = useMemo(() => {
    const r = [];
    const u = [];
    let overdue = 0;
    contacts.forEach((c) => {
      const score = c.ai_insight?.fit_score;
      if (typeof score === "number") {
        r.push(c);
        const d = daysSinceContact(c.last_contacted_at);
        if (d == null || d > 30) overdue += 1;
      } else {
        u.push(c);
      }
    });
    // Stable sort by score so labels collide less
    r.sort((a, b) => (a.ai_insight.fit_score || 0) - (b.ai_insight.fit_score || 0));
    return { ranked: r, unranked: u, overdueCount: overdue };
  }, [contacts]);

  const selected = contacts.find((c) => c.id === selectedId) || null;

  return (
    <div data-testid="career-path-view" className="space-y-5">
      {/* Header / legend */}
      <div className="rm-card-static p-5 md:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="rm-label mb-1" style={{ color: "#FF6A00" }}>· Your career path, paved with people</div>
            <h2 className="font-display text-2xl uppercase">Stones on the path.</h2>
            <p className="text-sm mt-1 max-w-xl" style={{ color: "#475569" }}>
              X-axis is how well each contact fits your career path (run "Why
              this person matters" to score them). Colour shows when you last
              touched them.
            </p>
          </div>
          {overdueCount > 0 && (
            <div
              className="rm-card-static px-3 py-2"
              style={{ background: "#FFFBEB", border: "3px dashed #F59E0B" }}
              data-testid="career-path-overdue"
            >
              <div className="flex items-center gap-2">
                <AlertTriangle size={14} style={{ color: "#92400E" }} />
                <div className="font-mono-rm text-[11px] uppercase tracking-widest" style={{ color: "#92400E", fontWeight: 700 }}>
                  {overdueCount} stone{overdueCount === 1 ? "" : "s"} overdue
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Staleness legend */}
        <div className="flex flex-wrap gap-3 mt-4">
          {[
            { color: "#10B981", label: "≤14 days" },
            { color: "#FFC145", label: "15–30" },
            { color: "#F59E0B", label: "31–60" },
            { color: "#EF4444", label: "60+ cold" },
            { color: "#0F1B2D", label: "Never" },
          ].map((l) => (
            <div key={l.label} className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full" style={{ background: l.color, border: "1.5px solid #0F1B2D" }} />
              <span className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#475569" }}>{l.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Unranked rail */}
      {unranked.length > 0 && (
        <div className="rm-card-static p-4 md:p-5" data-testid="career-path-unranked-rail">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div>
              <div className="rm-label" style={{ color: "#94A3B8" }}>· Unranked rail</div>
              <div className="font-display text-sm uppercase" style={{ color: "#0F1B2D" }}>
                {unranked.length} stone{unranked.length === 1 ? "" : "s"} waiting to be analysed
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {unranked.map((c) => (
              <UnrankedStone
                key={c.id}
                contact={c}
                onAnalyzed={onChanged}
                onSelect={() => setSelectedId(c.id)}
                selected={c.id === selectedId}
              />
            ))}
          </div>
        </div>
      )}

      {/* The path */}
      <div className="rm-card-static p-4 md:p-5 overflow-x-auto" data-testid="career-path-track">
        <PathTrack
          contacts={ranked}
          onSelect={(id) => setSelectedId(id === selectedId ? null : id)}
          selectedId={selectedId}
          hoverId={hoverId}
          setHoverId={setHoverId}
        />
      </div>

      {/* Selected-stone detail */}
      {selected && (
        <SelectedStoneCard contact={selected} onClose={() => setSelectedId(null)} onChanged={onChanged} />
      )}
    </div>
  );
}

// ── Unranked rail stone (the only place we run the analyze AI from path view) ──
function UnrankedStone({ contact, onAnalyzed, onSelect, selected }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const analyze = async (e) => {
    e.stopPropagation();
    setErr("");
    setBusy(true);
    try {
      await api.post(`/network/contacts/${contact.id}/analyze`);
      onAnalyzed?.();
    } catch (e2) {
      setErr(friendlyAxiosError(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === "Enter") onSelect(); }}
      className="rm-card-static p-2 px-3 cursor-pointer transition-transform"
      style={{
        background: "white",
        border: selected ? "3px solid #FF6A00" : "3px solid #0F1B2D",
        boxShadow: "2px 2px 0 0 #0F1B2D",
      }}
      data-testid={`unranked-stone-${contact.id}`}
      title={err || contact.name}
    >
      <div className="flex items-center gap-2">
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center font-display text-[11px]"
          style={{ background: "#FFFBEB", color: "#0F1B2D", border: "2px solid #0F1B2D" }}
        >
          {contact.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
        </div>
        <div className="text-xs font-semibold leading-tight max-w-[160px] truncate">{contact.name}</div>
        <button
          onClick={analyze}
          disabled={busy}
          className="font-mono-rm text-[9px] uppercase tracking-widest px-1.5 py-0.5 ml-1"
          style={{
            background: "#FF6A00",
            color: "white",
            border: "2px solid #0F1B2D",
            borderRadius: 4,
            fontWeight: 700,
          }}
          data-testid={`unranked-analyze-${contact.id}`}
          title="Analyze (10 credits)"
        >
          {busy ? <Loader size={9} className="animate-spin inline" /> : <Sparkles size={9} className="inline" />}
        </button>
      </div>
    </div>
  );
}

// ── Path track (SVG) ──
function PathTrack({ contacts, onSelect, selectedId, hoverId, setHoverId }) {
  // Inner padding so stones near 0 or 100 aren't clipped
  const innerPadX = 28;
  // We size the SVG via percentage width; pick a virtual viewbox so collision
  // detection (y-jitter) is consistent regardless of screen width.
  const VBW = 1000;
  const VBH = TRACK_HEIGHT;

  // Jitter Y to reduce stone overlap when scores cluster. Even-index stones
  // sit slightly above the centerline, odd-index slightly below.
  const positionFor = (i, score) => {
    const x = innerPadX + ((VBW - innerPadX * 2) * (score / 100));
    const yOffset = (i % 2 === 0) ? -10 : 10;
    return { x, y: VBH / 2 + yOffset };
  };

  return (
    <div className="min-w-[680px]">
      <svg
        viewBox={`0 0 ${VBW} ${VBH + 40}`}
        width="100%"
        height={TRACK_HEIGHT + 40}
        style={{ display: "block" }}
        data-testid="career-path-svg"
      >
        {/* Zone backgrounds */}
        {PATH_ZONES.map((z) => {
          const x = innerPadX + ((VBW - innerPadX * 2) * (z.min / 100));
          const w = ((VBW - innerPadX * 2) * ((z.max - z.min + 1) / 100));
          return (
            <g key={z.id}>
              <rect x={x} y={VBH / 2 - 26} width={w} height={52} fill={z.accent} opacity={0.12} />
              <text
                x={x + w / 2}
                y={VBH / 2 + 50}
                fill={z.accent}
                fontFamily="ui-monospace, SF Mono, monospace"
                fontSize={11}
                fontWeight={700}
                letterSpacing={1.4}
                textAnchor="middle"
              >
                {z.label.toUpperCase()}
              </text>
            </g>
          );
        })}
        {/* Centre line (the path itself) */}
        <line
          x1={innerPadX}
          y1={VBH / 2}
          x2={VBW - innerPadX}
          y2={VBH / 2}
          stroke="#0F1B2D"
          strokeWidth={2}
          strokeDasharray="6 6"
        />
        {/* Endpoint markers */}
        <circle cx={innerPadX} cy={VBH / 2} r={5} fill="#0F1B2D" />
        <g transform={`translate(${VBW - innerPadX}, ${VBH / 2})`}>
          <circle r={6} fill="#FF6A00" stroke="#0F1B2D" strokeWidth={2} />
        </g>

        {/* Stones */}
        {contacts.length === 0 ? (
          <text
            x={VBW / 2}
            y={VBH / 2}
            fill="#94A3B8"
            fontFamily="ui-monospace, SF Mono, monospace"
            fontSize={12}
            textAnchor="middle"
            dominantBaseline="central"
          >
            Analyze a contact to plot it on the path →
          </text>
        ) : (
          contacts.map((c, i) => {
            const score = c.ai_insight.fit_score;
            const { x, y } = positionFor(i, score);
            const days = daysSinceContact(c.last_contacted_at);
            const tint = stalenessTint(days);
            const initials = c.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
            const isSel = c.id === selectedId;
            const isHover = c.id === hoverId;
            return (
              <g
                key={c.id}
                onClick={() => onSelect(c.id)}
                onMouseEnter={() => setHoverId(c.id)}
                onMouseLeave={() => setHoverId(null)}
                style={{ cursor: "pointer" }}
                data-testid={`path-stone-${c.id}`}
              >
                <circle
                  cx={x}
                  cy={y}
                  r={STONE_RADIUS + (isSel ? 4 : 0)}
                  fill={tint.color}
                  stroke="#0F1B2D"
                  strokeWidth={isSel ? 4 : 3}
                />
                <text
                  x={x}
                  y={y}
                  fill="white"
                  fontFamily="'Inter', sans-serif"
                  fontSize={11}
                  fontWeight={800}
                  textAnchor="middle"
                  dominantBaseline="central"
                  style={{ pointerEvents: "none" }}
                >
                  {score}
                </text>
                {/* Hover/selected label */}
                {(isSel || isHover) && (
                  <g>
                    <rect
                      x={x - 60}
                      y={y - (i % 2 === 0 ? STONE_RADIUS + 28 : -STONE_RADIUS - 6)}
                      width={120}
                      height={22}
                      fill="#0F1B2D"
                      rx={3}
                    />
                    <text
                      x={x}
                      y={y - (i % 2 === 0 ? STONE_RADIUS + 14 : -STONE_RADIUS - 20)}
                      fill="white"
                      fontFamily="'Inter', sans-serif"
                      fontSize={11}
                      fontWeight={700}
                      textAnchor="middle"
                      dominantBaseline="central"
                      style={{ pointerEvents: "none" }}
                    >
                      {initials} · {c.name.length > 14 ? `${c.name.slice(0, 14)}…` : c.name}
                    </text>
                  </g>
                )}
              </g>
            );
          })
        )}
      </svg>
    </div>
  );
}

function SelectedStoneCard({ contact, onClose, onChanged }) {
  const [marking, setMarking] = useState(false);
  const [last, setLast] = useState(contact.last_contacted_at);
  const days = daysSinceContact(last);
  const tint = stalenessTint(days);
  const ins = contact.ai_insight || {};
  const zone = zoneForScore(ins.fit_score);

  const markTouched = async () => {
    setMarking(true);
    try {
      const { data } = await api.post(`/network/contacts/${contact.id}/contacted`);
      setLast(data.last_contacted_at);
      onChanged?.();
    } catch { /* ignore */ }
    setMarking(false);
  };

  return (
    <div
      className="rm-card-static p-5 md:p-6"
      style={{ borderColor: tint.color }}
      data-testid={`path-selected-${contact.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center font-display text-xl"
            style={{ background: tint.color, color: "white", border: "3px solid #0F1B2D", boxShadow: "2px 2px 0 0 #0F1B2D" }}
          >
            {ins.fit_score ?? "—"}
          </div>
          <div>
            <div className="font-display text-xl uppercase leading-tight">{contact.name}</div>
            <div className="text-xs" style={{ color: "#475569" }}>
              {contact.role || "—"}{contact.company ? ` · ${contact.company}` : ""}
            </div>
            <div className="flex flex-wrap gap-x-2 mt-1">
              {zone && (
                <span className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: zone.accent, fontWeight: 700 }}>
                  · {zone.label}
                </span>
              )}
              <span className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: tint.color, fontWeight: 700 }}>
                · {tint.label}
              </span>
              {contact.linkedin_url && (
                <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer"
                   className="text-[11px] inline-flex items-center gap-1"
                   style={{ color: "#FF6A00" }}>
                  LinkedIn <ExternalLink size={10} />
                </a>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={markTouched}
            disabled={marking}
            className="rm-btn text-xs px-3 py-2"
            style={{ background: "#0F1B2D", color: "#FFC145", border: "3px solid #0F1B2D", fontWeight: 700 }}
            data-testid={`path-touched-${contact.id}`}
          >
            {marking ? <Loader size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
            Touched today
          </button>
          <button onClick={onClose} className="rm-btn rm-btn-ghost text-xs px-3 py-2"
                  data-testid={`path-selected-close`}>
            <ChevronDown size={13} /> Close
          </button>
        </div>
      </div>

      {ins.why_matters && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 text-sm">
          <div>
            <div className="rm-label" style={{ color: "#FF6A00" }}>Why they matter</div>
            <p>{ins.why_matters}</p>
          </div>
          {ins.first_ask && (
            <div>
              <div className="rm-label" style={{ color: "#065F46" }}>Your highest-leverage ask</div>
              <p className="italic">"{ins.first_ask}"</p>
            </div>
          )}
          {ins.warm_opener && (
            <div className="md:col-span-2 p-3 rounded"
                 style={{ background: "#FFFBEB", border: "2px dashed #FFC145" }}>
              <div className="rm-label mb-0.5" style={{ color: "#92400E" }}>DM-able opener</div>
              <p>{ins.warm_opener}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
